import React from 'react'
import type { PageTagSource } from './page-tag-source'
import type { FieldValueLoader } from './field-value-loader'
import type { NotificationSource } from './notification-source'

/**
 * A prompt a condition may target, resolved to the label the reader picks it by.
 *
 * The whole reason the list is loaded eagerly is that a stored condition holds only
 * an id, and an id on its own is not something a reader recognises. Each id therefore
 * arrives paired with the name it resolves to, deleted prompts included, so a segment
 * saved against one still reads as `#{id}: {name}` rather than a bare number.
 */
export interface PromptOption {
    id: number
    name: string
}

/**
 * A journey a journey-status condition may target, resolved to the name it is picked by.
 *
 * Resolved the same way, and for the same reason, as a prompt: a stored condition holds only a
 * journey id, so the list is what turns that id back into a name the reader recognizes.
 */
export interface JourneyOption {
    id: number
    name: string
}

/**
 * What a segment value input needs that the query builder cannot tell it.
 *
 * The builder hands a value input the condition and the field, and nothing else —
 * it knows nothing of the domain a segment is scoped to or of the vocabularies its
 * conditions draw on. Anything a renderer needs from outside the condition arrives
 * here, so the renderers stay a function of their inputs and a caller can state
 * those inputs directly.
 */
export interface SegmentValueInputContextValue {
    /** The symbol counts of money are stated in. */
    currencySymbol: string
    /**
     * The prompts a prompt condition may target, each already resolved to its label.
     * Loaded once for the whole builder — a segment can hold several prompt conditions,
     * and a fetch per row would repeat the same read for each of them.
     */
    prompts: PromptOption[]
    /** True while the prompt list is still on its way. */
    promptsLoading: boolean
    /**
     * The journeys a journey-status condition may target, each resolved to its name. Loaded once
     * for the whole builder, for the same reason as the prompt list — a segment can hold several
     * journey conditions, and a fetch per row would repeat the same read.
     */
    journeys: JourneyOption[]
    /** True while the journey list is still on its way. */
    journeysLoading: boolean
    /** Where page tags are read from and recorded. Absent where nothing can serve them. */
    pageTags?: PageTagSource
    /**
     * Reads a field's own values, a page at a time, from wherever that field's values live.
     *
     * Absent where nothing can serve them, which a caller treats as a field having only the values
     * its catalog entry arrived with — never as a reason to show an empty list as though it were
     * complete.
     */
    loadFieldValues?: FieldValueLoader
    /**
     * Where notifications are searched from. Absent where nothing can serve them, so a
     * renderer with no source above it searches nothing rather than reaching for a server.
     */
    notifications?: NotificationSource
    /**
     * The notification titles resolved so far, keyed by the id the condition stores.
     *
     * A notification's title is only ever resolved by a search, so it arrives while the control
     * that asked for it is open. A collapsed row has to read the same titles without mounting that
     * control, which is why they are held here rather than inside it. An id with no entry has not
     * been resolved and reads as itself.
     */
    notificationTitles?: Record<string, string>
    /**
     * Records titles as they resolve, so every row reads the ones already known.
     *
     * Absent where nothing is holding them, which a renderer treats as having nowhere to record —
     * never as a reason to withhold what it resolved from its own list.
     */
    recordNotificationTitles?: (titles: Record<string, string>) => void
    /**
     * The query as it currently stands.
     *
     * A control whose vocabulary is fetched a page at a time reads this to make sure
     * everything the segment already names is offered, however deep in that
     * vocabulary it sits. A function rather than a value because the document lives
     * outside React state: reading it when it is needed is what keeps it current.
     */
    getDocument?: () => Record<string, unknown>
}

/**
 * The values a renderer falls back on when nothing supplies them.
 *
 * A currency is always needed and never blocking, so it defaults the way the
 * platform's own currency lookup does. A vocabulary that has not arrived is
 * stated as empty and settled rather than as loading, because a renderer with no
 * provider above it is never going to be given one.
 */
const FALLBACK: SegmentValueInputContextValue = {
    currencySymbol: '$',
    prompts: [],
    promptsLoading: false,
    journeys: [],
    journeysLoading: false,
}

export const SegmentValueInputContext = React.createContext<SegmentValueInputContextValue>(FALLBACK)

export function useSegmentValueInputContext(): SegmentValueInputContextValue {
    return React.useContext(SegmentValueInputContext)
}
